/**
 * Whether the person is actually here, from what the browser can see.
 *
 * A tab left open all night keeps syncing, so Matrix keeps calling that person
 * online. `ChatClient.announcePresence` is what fixes it, but the SDK cannot
 * decide when to call itself: a cron job calling `timeline()` looks exactly
 * like somebody reading. Only the app sees the Page Visibility API and real
 * keystrokes, so the judgement lives here (wtfalch/chat#20, agora#260).
 *
 * **One timer, not one per event.** Listeners only write down when the last
 * real thing happened; a single interval decides what that means. A mouse
 * moving across the window would otherwise clear and rebuild a timeout on
 * every frame.
 *
 * **Transitions only.** `onChange` fires when the answer changes, never to
 * repeat itself, because each call is a request the SDK sends.
 */

export type Idleness = 'online' | 'away';

/** Real input, as opposed to the page doing things to itself. `pointermove`
 *  is in: somebody moving the mouse is here, and the single timer makes its
 *  rate free. `scroll` is not -- the page scrolls itself. */
const ACTIVITY = ['keydown', 'pointerdown', 'pointermove', 'wheel', 'focus'] as const;

/** Five minutes of nothing is the usual line between "reading" and "gone",
 *  and it sits well inside Synapse's own `idle_timeout`, which moves a quiet
 *  session on server-side after this client has stopped saying otherwise. */
const QUIET_MS = 5 * 60 * 1000;

/**
 * Watches the tab and calls `onChange` each time the answer changes. The
 * person starts `'online'`, which is what a fresh sync already says, so the
 * first call is always the first move away from that.
 *
 * Returns the function that stops watching.
 */
export function watchIdleness({
  onChange,
  quietMs = QUIET_MS,
  /** How often the one timer asks. A quarter of the quiet window keeps the
   *  answer no more than that late, at 4 wake-ups per window. */
  checkMs = Math.max(1000, Math.floor(quietMs / 4)),
}: {
  onChange: (next: Idleness) => void;
  quietMs?: number;
  checkMs?: number;
}): () => void {
  let last = Date.now();
  let reported: Idleness = 'online';

  const now = (): Idleness => (document.hidden || Date.now() - last >= quietMs ? 'away' : 'online');

  const settle = () => {
    const next = now();
    if (next === reported) return;
    reported = next;
    onChange(next);
  };

  // A hidden tab is away at once -- there is nothing to wait for, the person
  // is looking at something else.
  const onVisibility = () => {
    if (!document.hidden) last = Date.now();
    settle();
  };

  const onActivity = () => {
    last = Date.now();
    settle();
  };

  for (const type of ACTIVITY) window.addEventListener(type, onActivity, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  const timer = setInterval(settle, checkMs);

  return () => {
    for (const type of ACTIVITY) window.removeEventListener(type, onActivity);
    document.removeEventListener('visibilitychange', onVisibility);
    clearInterval(timer);
  };
}
